import { MapPin } from 'lucide-react';
import type {
  BillboardDetailData,
  BillboardMedia,
} from '@/features/billboards/api/billboardApi';
import { BookingPanel } from '@/features/billboards/components/detail/BookingPanel';
import { MediaGallery } from '@/features/billboards/components/detail/MediaGallery';
import { formatScreenName } from '@/features/billboards/utils/billboardDetailUtils';
import { buildScreenLocation } from '@/features/billboards/utils/billboardsListUtils';

type DetailHeroProps = {
  billboard: BillboardDetailData;
  media: BillboardMedia[];
  selectedImage: number;
  onSelectImage: (index: number) => void;
  currency: string;
  startDate: string;
  minStartDate: string;
  isScreenOffline?: boolean;
  hasToken: boolean;
  canCreate: boolean;
  isClient?: boolean;
  onStartDateChange: (value: string) => void;
  onCreate: () => void;
};

export function DetailHero({
  billboard,
  media,
  selectedImage,
  onSelectImage,
  currency,
  startDate,
  minStartDate,
  isScreenOffline = false,
  hasToken,
  canCreate,
  isClient = false,
  onStartDateChange,
  onCreate,
}: DetailHeroProps) {
  const locationLabel = buildScreenLocation(billboard) || 'Location unavailable';

  /* Modified by: Ishan | Soft-contrast hero header and 2-column card layout */
  return (
    <div className="w-full">
      <div className="mb-5 text-left">
        <h1 className="m-0 text-2xl font-extrabold tracking-tight break-words text-[#3A3660] sm:text-[26px]">
          {formatScreenName(billboard.screenName, 'Screen')}
        </h1>
        <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#6A6688]">
          <MapPin size={14} className="shrink-0 text-[#8156B4]" />
          <span className="truncate">{locationLabel}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        {/* Modified by: Ishan | Soft-contrast media gallery card */}
        <div className="bg-white rounded-[24px] p-[22px] shadow-[0_10px_30px_rgba(60,40,120,0.05)] border border-[#EEEBF8]">
          <MediaGallery
            media={media}
            activeIndex={selectedImage}
            screenName={formatScreenName(billboard.screenName, 'Screen')}
            onSelect={onSelectImage}
          />
        </div>
        <BookingPanel
          basePrice={billboard.basePrice}
          currency={currency}
          startDate={startDate}
          minStartDate={minStartDate}
          isScreenOffline={isScreenOffline}
          hasToken={hasToken}
          canCreate={canCreate}
          isClient={isClient}
          onStartDateChange={onStartDateChange}
          onCreate={onCreate}
          billboard={billboard}
        />
      </div>
    </div>
  );
}
